/**
 * API 配置文件
 * 集中管理 API URL 和其他配置
 */

// 开发环境默认配置
const DEFAULT_API_BASE_URL = 'http://localhost:5297/api';
const DEFAULT_UPLOADS_BASE_URL = 'http://localhost:5297/uploads';  // 直接包含 /uploads

// 生产环境配置
const PROD_API_BASE_URL = 'https://qingsuanccp.org/api';  // 确保以 /api 结尾
const PROD_UPLOADS_BASE_URL = 'https://qingsuanccp.org/uploads';  // 直接包含 /uploads

// 判断当前环境
const isProd = window.location.hostname === 'qingsuanccp.org';

// 获取环境变量，如果不存在则使用默认值
const getEnvVar = (key, defaultValue) => {
  try {
    // 优先使用 Vite 注入的环境变量
    if (import.meta && import.meta.env && import.meta.env[key]) {
      return import.meta.env[key];
    }

    // 如果没有环境变量，则根据当前环境使用对应配置
    if (isProd) {
      switch (key) {
        case 'VITE_API_BASE_URL':
          return PROD_API_BASE_URL;
        case 'VITE_UPLOADS_BASE_URL':
          return PROD_UPLOADS_BASE_URL;
        default:
          return defaultValue;
      }
    }

    return defaultValue;
  } catch (error) {
    console.warn(`无法读取环境变量 ${key}，使用默认值: ${defaultValue}`);
    return defaultValue;
  }
};

// 获取基础 URL
const baseUrl = getEnvVar('VITE_API_BASE_URL', DEFAULT_API_BASE_URL);
const uploadsBaseUrl = getEnvVar('VITE_UPLOADS_BASE_URL', DEFAULT_UPLOADS_BASE_URL);

// 打印当前环境配置（仅在开发环境）
if (!isProd) {
  console.log('当前环境配置:', {
    baseUrl,
    uploadsBaseUrl,  // 直接使用环境变量，不再添加 /uploads
    isProd,
    hostname: window.location.hostname,
    env: import.meta?.env ? {
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      VITE_UPLOADS_BASE_URL: import.meta.env.VITE_UPLOADS_BASE_URL,
      MODE: import.meta.env.MODE
    } : 'import.meta.env not available'
  });
}

// 工具函数：标准化 URL 路径
const normalizePath = (path) => {
  if (!path) return '';

  console.log('normalizePath - 原始路径:', path);

  // 移除所有开头的斜杠
  let normalized = path.trim();
  while (normalized.startsWith('/')) {
    normalized = normalized.substring(1);
  }
  console.log('normalizePath - 移除开头斜杠后:', normalized);

  // 检查路径是否以 uploads/ 开头
  // 如果是，则移除它，因为 baseUrl 已经包含了 uploads
  const uploadsPattern = /^(?:uploads\/?)/i;
  if (uploadsPattern.test(normalized)) {
    normalized = normalized.replace(uploadsPattern, '');
    console.log('normalizePath - 移除了uploads前缀后:', normalized);
  }

  // 移除所有连续的斜杠
  normalized = normalized.replace(/\/+/g, '/');
  console.log('normalizePath - 最终标准化路径:', normalized);

  return normalized;
};

// 工具函数：构建完整的文件 URL
const buildFileUrl = (baseUrl, path) => {
  if (!path) return '';

  // 添加调试日志
  console.log('buildFileUrl - 输入参数:', { baseUrl, path });

  // 如果已经是完整 URL，直接返回
  if (path.startsWith('http://') || path.startsWith('https://')) {
    console.log('buildFileUrl - 已是完整URL，直接返回:', path);
    return path;
  }

  // 确保 baseUrl 是绝对 URL
  let normalizedBase = baseUrl;

  // 如果 baseUrl 不是以 http:// 或 https:// 开头，则认为它是相对路径
  if (!normalizedBase.startsWith('http://') && !normalizedBase.startsWith('https://')) {
    // 使用当前域名构建绝对 URL
    const origin = window.location.origin;
    // 如果 baseUrl 不是以 / 开头，添加 /
    if (!normalizedBase.startsWith('/')) {
      normalizedBase = '/' + normalizedBase;
    }
    normalizedBase = origin + normalizedBase;
    console.log('buildFileUrl - 转换为绝对URL:', normalizedBase);
  }

  // 移除末尾的斜杠
  normalizedBase = normalizedBase.replace(/\/+$/, '');
  console.log('buildFileUrl - 处理后的baseUrl:', normalizedBase);

  // 如果路径是根路径，直接使用 baseUrl
  if (path === '/') {
    console.log('buildFileUrl - 路径是根路径，返回:', normalizedBase);
    return normalizedBase;
  }

  // 标准化路径
  const normalizedPath = normalizePath(path);
  console.log('buildFileUrl - 标准化后的路径:', normalizedPath);

  // 构建最终 URL
  const finalUrl = `${normalizedBase}/${normalizedPath}`;
  console.log('buildFileUrl - 最终URL:', finalUrl);
  return finalUrl;
};

// API 配置
const config = {
  baseUrl,
  uploadsBaseUrl,  // 直接使用环境变量，不再添加 /uploads

  // 添加工具函数
  normalizePath,
  buildFileUrl,

  // 认证相关 API
  auth: {
    base: `${baseUrl}/auth`,
    login: `${baseUrl}/auth/login`,
    logout: `${baseUrl}/auth/logout`
  },

  // 文档相关 API
  documents: {
    base: `${baseUrl}/documents`,
    byId: (id) => `${baseUrl}/documents/${id}`,
    byType: (type) => `${baseUrl}/documents/type/${type}`,
    uploadThumbnail: (id) => `${baseUrl}/documents/${id}/thumbnail`,
    deleteThumbnail: (id) => `${baseUrl}/documents/${id}/thumbnail`
  },

  // 附件相关 API
  attachments: {
    base: `${baseUrl}/attachments`,
    byId: (id) => `${baseUrl}/attachments/${id}`,
    byDocument: (documentId) => `${baseUrl}/attachments/document/${documentId}`,
    upload: `${baseUrl}/attachments/simple-upload`,
    test: `${baseUrl}/attachments/test`
  }
};

export default config;
